import React from 'react';
import { TierTwoBrokeragePlanSVG } from '@bb/elements-web';
import { ViewingLevel } from 'Router/pages/SubscriptionsPaymentsPage';
import SubscriptionCard, {
  SubscriptionCardConfigType,
} from './SubscriptionCard';

export type PlanTier = 'tier1' | 'tier2';

export type OrgPlanPayer = 'boardOrMls' | 'brokerage';

export interface OrgPlanCardProps {
  /** The plan tier - affects title, description, button text, and status pill */
  currentOrgPlan: PlanTier;
  /** How the Org Plan subscription is billed - affects text next to status pill */
  orgPlanPayer: OrgPlanPayer;
  /** Whether this is an indivdual user or for a brokerage viewing the page */
  viewingLevel: ViewingLevel;
  /** Callback when button is clicked */
  onButtonClick?: () => void;
}

/** The text to show next to the status label */
const billingTypeLabels: Record<OrgPlanPayer, string> = {
  boardOrMls: 'Managed by Organization',
  brokerage: 'Managed by Brokerage',
};

/**
 * The card displaying information about the Org Plan,
 * either Agent Plan (Tier 1) or Brokerage Plan (Tier 2),
 * that the user's org pays for.
 */
const OrgPlanCard: React.FC<OrgPlanCardProps> = ({
  currentOrgPlan,
  orgPlanPayer,
  viewingLevel,
  onButtonClick,
}) => {
  // Only brokerages can upgrade the Org Plan when the board/MLS isn't paying for it.
  const allowedToSelfUpgrade =
    viewingLevel === 'org' && orgPlanPayer === 'brokerage';

  /** The config we pass in depending on which level of Org Plan card to show */
  const tierConfig: Record<PlanTier, SubscriptionCardConfigType> = {
    tier1: {
      planName: 'Tier 1 Agent Plan',
      description:
        'A complete showing and admin management system designed for individual agents.',
      statusText: allowedToSelfUpgrade ? 'Upgrade Available' : 'Active',
      isUpgradeAvailable: true,
    },
    tier2: {
      planName: 'Tier 2 Brokerage Plan',
      description:
        'A complete showing and admin management system designed for brokerages.',
      statusText: 'Active',
      isUpgradeAvailable: false,
    },
  };

  const config = tierConfig[currentOrgPlan];
  config.isSuspended = false;
  config.suspensionTooltipText =
    'This will be removed when removing the Org Plan card.';
  const managedByText = billingTypeLabels[orgPlanPayer];

  return (
    <SubscriptionCard
      // There is currently no way for users to be unsubscribed
      // from both Tier 1 & Tier 2. Default this to true.
      isSubscribed
      isUpgradeAvailable={config.isUpgradeAvailable}
      allowedToSelfUpgrade={allowedToSelfUpgrade}
      image={<TierTwoBrokeragePlanSVG />}
      statusText={config.statusText}
      isSuspended={config.isSuspended}
      suspensionTooltipText={config.suspensionTooltipText}
      managedByText={managedByText}
      upgradeText="Upgrade to Tier 2 Brokerage"
      planName={config.planName}
      description={config.description}
      onButtonClick={onButtonClick}
    />
  );
};

export default OrgPlanCard;
